import React, { useState, useEffect, useRef } from "react";
import { Formik, Form } from "formik";
import { Button } from "devextreme-react/button";
import { SelectBox, DateBox, TextBox } from "devextreme-react";
import INTAKE_API from "api/intakeApi";

import { intakeSchema } from "./intakeSchema";
import { useNavigate } from "react-router-dom";
import { Toast } from "devextreme-react/toast";
import { useFetch, useFetchProducts, usePost } from "@hooks/useBatchIntakeApi";
import { useQueryClient } from "@tanstack/react-query";
import "../cards/DarkCard.scss";

const IntakeForm = () => {
  const { data: statuses } = useFetch("getStatus", INTAKE_API.getStatus, {
    PageNo: 0,
    PageSize: 1000,
  });
  const { data: suppliers } = useFetch(
    "getSuppliers",
    INTAKE_API.getSuppliers,
    { PageSize: 0, PageSize: 1000 }
  );
  const [visible, setVisible] = useState(false); // Default visible
  const [toastType, setToastType] = useState("error"); // Default error
  const [toastMessage, setToastMessage] = useState(""); // Default message
  const [loading, setLoading] = useState(false); // Add loading state
  const [isOtherSelected, setIsOtherSelected] = useState(true); // Track if "Other" is selected
  const [isOtherSupplier, setIsOtherSupplier] = useState(false); // Track if "Other" is selected for Supplier
  const [isOtherProduct, setIsOtherProduct] = useState(false); // Track if "Other" is selected for Product
  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef(null);

  const showToast = (type, message) => {
    setToastType(type);
    setToastMessage(message);
    setVisible(true);
  };
  console.log("suppliers", suppliers);

  const [supplierID, setSupplierID] = useState(null);
  const [productID, setProductID] = useState(null); // Add state for productID

  const { data: products } = useFetchProducts(
    "getProducts", // Always call the product API
    INTAKE_API.getProducts,
    { PageNo: 0, PageSize: 1000 } // Remove dependency on supplierID
  );

  const { data: sizes } = useFetch(
    productID && productID !== "Other" ? `getProductsSize_${productID}` : null, // Avoid API call if "Other" is selected
    INTAKE_API.getProductsSize,
    productID && productID !== "Other" ? { ProductId: productID } : undefined
  );

  const { data: qualities } = useFetch(
    productID && productID !== "Other"
      ? `getProductsQuality_${productID}`
      : null, // Avoid API call if "Other" is selected
    INTAKE_API.getProductsQuality,
    productID && productID !== "Other" ? { ProductId: productID } : undefined
  );

  const setFieldValueRef = useRef(null); // Ref to store setFieldValue

  useEffect(() => {
    if (sizes?.data?.length > 0 && setFieldValueRef.current) {
      setFieldValueRef.current("productSizeID", sizes.data[0].productSizeID); // Auto-select the first size
    }
  }, [sizes]);

  useEffect(() => {
    if (qualities?.data?.length > 0 && setFieldValueRef.current) {
      setFieldValueRef.current(
        "productQualityID",
        qualities.data[0].productQualityID
      ); // Auto-select the first quality
    }
  }, [qualities]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, []);

  const batchIntakeMutation = usePost("batchIntake", INTAKE_API.batchIntake);
  const productMutation = usePost("addProducts", INTAKE_API.addProduct);
  const supplierMutation = usePost("addSupplier", INTAKE_API.addSupplier);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return (
    <>
      <Toast
        visible={visible}
        message={toastMessage}
        type={toastType} // success, error, warning, info
        displayTime={2000} // 3 seconds
        onHiding={() => setVisible(false)}
        position={{ my: "top right", at: "top right", offset: "10 10" }}
      />

      <Formik
        initialValues={{
          supplierID: null,
          intakeDate: "",
          statusID: null,
          claim: "",
          pricePerKG: null,
          volume: null,
          claimAmount: null,
          outputProduct: "",
          salePricePerKG: "",
          productID: null,
          productSizeID: null,
          productQualityID: null,
          locationID: null,
          purchaseRef: "",
          batchNumber: "",
          otherSupplierName: "", // Add field for "Other" Supplier
          otherProductName: "", // Add field for "Other" Product Name
          otherProductSize: "", // Add field for "Other" Product Size
          otherProductQuality: "", // Add field for "Other" Product Quality
          otherLocation: "", // Add field for "Other" Location
        }}
        validationSchema={intakeSchema}
        onSubmit={async (values) => {
          setLoading(true); // Start loading
          console.log("values", values);

          try {
            let supplierID = values.supplierID;
            let productID = values.productID;
            let locID = values.locationID;
            let productSizeID = values.productSizeID;
            let productQualityID = values.productQualityID;

            // Handle "Other" supplier
            if (isOtherSupplier) {
              const supplierPayload = {
                name: values.otherSupplierName,
                supplierNumber: "",
                contactInfo: "",

                supplierAddressRequest: [
                  {
                    addressLine: values.locationID, // Use location as addressLine
                    name: values.locationID,
                    contactInfo: "",
                    addressType: "",
                    country: "",
                  },
                ],
              };
              console.log("suppliierPayload", supplierPayload);

              try {
                const supplierResponse =
                  await supplierMutation.mutateAsync(supplierPayload);
                supplierID = supplierResponse?.data?.supplierId;
                locID = supplierResponse?.data?.supplierAddressIds[0];
              } catch (error) {
                console.error("Error adding supplier:", error);
                showToast(
                  "error",
                  error?.response?.data?.errors[0]?.errorMessageEn ||
                    "Failed to add supplier. Please try again."
                );
                setLoading(false); // Stop loading
                return; // Exit the function
              }
            }
            // Handle "Other" product

            if (isOtherProduct) {
              const productPayload = {
                supplierID,
                productNumber: "",
                name: values.otherProductName
                  ? values.otherProductName
                  : values.productID,
                description: "",

                addProductSizeRequests: [
                  {
                    productSizeCode: "",
                    description: values.productSizeID,
                  },
                ],
                addProductQualityRequests: [
                  {
                    productQualityCode: "",
                    description: values.productQualityID,
                  },
                ],
              };

              try {
                const productResponse =
                  await productMutation.mutateAsync(productPayload);
                productID = productResponse?.data?.productId;
                productSizeID = productResponse?.data?.productSizeIds[0];
                productQualityID = productResponse?.data?.productQualityIds[0];
              } catch (error) {
                console.error("Error adding product:", error);
                showToast(
                  "error",
                  error?.response?.data?.errors[0]?.errorMessageEn ||
                    "Failed to add product. Please try again."
                );
                setLoading(false); // Stop loading
                return; // Exit the function
              }
            }

            // Prepare filtered values
            const filteredValues = Object.fromEntries(
              Object.entries(values).filter(
                ([key, value]) => value !== null && value !== ""
              )
            );

            delete filteredValues.availableLocations; // Remove this field if not needed
            filteredValues.supplierID = supplierID; // Use updated supplierID
            filteredValues.productID = productID; // Use updated productID
            filteredValues.locationID = locID;
            filteredValues.productSizeID = productSizeID; // Use updated productSizeID
            filteredValues.productQualityID = productQualityID; // Use updated productQualityID
            console.log("submitting", filteredValues);

            // Submit the form
            batchIntakeMutation.mutate(filteredValues, {
              onSuccess: (response) => {
                console.log("Form Submitted Successfully:", response);
                showToast("success", "Batch Intake added successfully");

                setTimeout(() => {
                  navigate("/quality/fillet/overview", { state: true });
                  setLoading(false); // Stop loading
                }, 2000);
              },
              onError: (error) => {
                setLoading(false); // Stop loading
                console.error("Error submitting form:", error);
                showToast("error", "Failed to add Batch Intake");
              },
            });
          } catch (error) {
            setLoading(false); // Stop loading
            console.error("Error handling 'Other' supplier or product:", error);
            showToast("error", "Failed to process 'Other' supplier or product");
          }
        }}
      >
        {({ values, setFieldValue, errors, touched }) => {
          setFieldValueRef.current = setFieldValue; // Store setFieldValue in the ref

          return (
            <Form className="space-y-4 relative">
              {/* Section: New Batch Intake */}
              <div className="dx-card responsive-paddings pb-4 custom-base-accent">
                <div className="w-full flex items-center mb-2">
                  <p className="whitespace-nowrap text-xl font-medium">
                    New Batch Intake:
                  </p>
                  <div className="flex-grow h-px bg-gray-300 ml-2 light-bg"></div>
                </div>

                {/* Intake section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Suppliers */}
                  <div>
                    <label className="relative mb-1">
                      Supplier{" "}
                      <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                        *
                      </span>
                    </label>
                    <SelectBox
                      dataSource={[
                        ...(suppliers?.data?.map((supplier) => ({
                          ...supplier,
                          name:
                            supplier.name.charAt(0).toUpperCase() +
                            supplier.name.slice(1),
                        })) || []),
                        { name: "Other", supplierID: "Other" },
                      ]} // Ensure "Other" has a supplierID and capitalize names
                      displayExpr="name"
                      valueExpr="supplierID"
                      value={values.supplierID}
                          searchEnabled={true} // Enable search
                      showClearButton={true} // Allow deselection
                      onValueChanged={(e) => {
                        setFieldValue("supplierID", e.value);

                        // Check if "Other" is selected
                        const isOther =
                          e.component.option("selectedItem")?.name === "Other";
                        setIsOtherSupplier(isOther); // Show or hide the "Other" field

                        if (!isOther) {
                          setFieldValue("otherSupplierName", ""); // Clear the "Other" field
                          setFieldValue("otherLocation", ""); // Clear location field
                        }

                        // Find the selected supplier
                        const selectedSupplier = suppliers?.data?.find(
                          (s) => s.supplierID === e.value
                        );
                        const locations =
                          selectedSupplier?.supplierAddress || [];
                        setFieldValue("availableLocations", locations); // Set available locations
                        setSupplierID(e.value);
                      }}
                      isValid={!(errors.supplierID && touched.supplierID)}
                      validationError={
                        errors.supplierID
                          ? { message: errors.supplierID }
                          : null
                      }
                    />
                  </div>

                  {/* Conditionally render "Other" field or Date field */}
                  {isOtherSupplier && (
                    <>
                      <div>
                        <label className="relative mb-1">
                          Supplier Name
                          <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                            *
                          </span>
                        </label>
                        <TextBox
                          value={values.otherSupplierName}
                          onValueChanged={(e) =>
                            setFieldValue("otherSupplierName", e.value)
                          }
                          placeholder="Enter Supplier Name..."
                          isValid={
                            !(
                              errors.otherSupplierName &&
                              touched.otherSupplierName
                            )
                          }
                          validationError={
                            errors.otherSupplierName
                              ? { message: errors.otherSupplierName }
                              : null
                          }
                        />
                      </div>
                      {/* <div>
                                                <label className='mb-1'>Location</label>
                                                <TextBox
                                                    value={values.otherLocation}
                                                    onValueChanged={(e) => setFieldValue("otherLocation", e.value)}
                                                    placeholder="Enter Location..."
                                                    isValid={!(errors.otherLocation && touched.otherLocation)}
                                                    validationError={errors.otherLocation ? { message: errors.otherLocation } : null}
                                                />
                                            </div> */}
                    </>
                  )}
                  <div>
                    <label className="relative mb-1">
                      Date{" "}
                      <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                        *
                      </span>
                    </label>
                    <DateBox
                      value={values.intakeDate}
                      onValueChanged={(e) =>
                        setFieldValue("intakeDate", e.value)
                      }
                      isValid={!(errors.intakeDate && touched.intakeDate)}
                      validationError={
                        errors.intakeDate
                          ? { message: errors.intakeDate }
                          : null
                      }
                      max={new Date()}
                      displayFormat="yyyy-MM-dd"
                      openOnFieldClick={true}
                      showClearButton={true}
                      inputAttr={{ readOnly: true }} // This disables typing
                      onKeyDown={(e) => e.event?.preventDefault()} // Extra protection
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="relative mb-1">
                      Status{" "}
                      <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px] ">
                        *
                      </span>
                    </label>
                    <SelectBox
                      dataSource={(statuses?.data || []).filter(
                        (status) => status.description !== "Completed"
                      )} // Exclude "Completed" status
                      displayExpr="description"
                      valueExpr="orderStatusId"
                      value={values.statusID}
                      onValueChanged={(e) => setFieldValue("statusID", e.value)}
                      isValid={!(errors.statusID && touched.statusID)}
                      validationError={
                        errors.statusID ? { message: errors.statusID } : null
                      }
                    />
                  </div>

                  <div>
                    <label className="mb-1">Output Product</label>
                    <SelectBox
                      showClearButton={true} // <--- THIS allows deselection
                      dataSource={[
                        { id: 1, name: "Trim A" },
                        { id: 2, name: "Trim B" },
                        { id: 3, name: "Trim C" },
                        { id: 3, name: "Trim D" },
                        { id: 3, name: "Trim E" },
                      ]}
                      displayExpr="name"
                      valueExpr="name"
                      value={values.outputProduct}
                      onValueChanged={(e) =>
                        setFieldValue("outputProduct", e.value)
                      }
                      isValid={!(errors.outputProduct && touched.outputProduct)}
                      validationError={
                        errors.outputProduct
                          ? { message: errors.outputProduct }
                          : null
                      }
                    />
                  </div>
                  {[
                    { name: "pricePerKG", label: "Price / kg (€)" },
                    { name: "volume", label: "Volume (kg)" },
                  ].map(({ name, label }) => (
                    <div key={name}>
                      <label className="relative mb-1">
                        {label}{" "}
                        <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                          *
                        </span>
                      </label>
                      <TextBox
                        value={values[name]}
                        inputAttr={{ type: "number", step: "any", min: "1" }} // Allow decimal values
                        // Restricts to numbers only

                        onValueChanged={(e) => setFieldValue(name, e.value)}
                        isValid={!(errors[name] && touched[name])}
                        validationError={
                          errors[name] && touched[name]
                            ? { message: errors[name] }
                            : null
                        }
                      />
                    </div>
                  ))}
                </div>
                {/* Intake section */}
              </div>

              <div className="dx-card responsive-paddings pb-4 custom-base-accent">
                <div className="w-full flex items-center mb-2">
                  <p className="whitespace-nowrap text-xl font-medium">
                    Claim:
                  </p>
                  <div className="flex-grow h-px bg-gray-300 ml-2 light-bg"></div>
                </div>

                {/* claim section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="mb-1">Claim</label>
                    <SelectBox
                      dataSource={[
                        { id: 1, name: "Dead fish upon delivery" },
                        { id: 2, name: "Soft texture and poor flesh quality" },
                        { id: 3, name: "Discoloration or melanin spots" },
                        { id: 3, name: "Sea lice and skin wounds" },
                      ]}
                      displayExpr="name"
                      valueExpr="name"
                      showClearButton={true}
                      value={values.claim}
                      onValueChanged={(e) => setFieldValue("claim", e.value)}
                      isValid={!(errors.claim && touched.claim)}
                      validationError={
                        errors.claim ? { message: errors.claim } : null
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1">Claim Amount (€)</label>
                    <TextBox
                      inputAttr={{ type: "number", step: "any", min: "1" }} // Allow decimal values
                      value={values.claimAmount}
                      onValueChanged={(e) =>
                        setFieldValue("claimAmount", e.value)
                      }
                      isValid={!(errors.claimAmount && touched.claimAmount)} // Show validation only if touched
                      validationError={
                        errors.claimAmount && touched.claimAmount
                          ? { message: "Enter a valid amount" }
                          : null
                      }
                    />
                  </div>
                </div>
                {/* claim section */}
              </div>

              <div className="dx-card responsive-paddings pb-4 custom-base-accent">
                <div className="w-full flex items-center mb-2">
                  <p className="whitespace-nowrap text-xl font-medium">
                    Product:
                  </p>
                  <div className="flex-grow h-[0.5px] bg-gray-300 ml-2 light-bg"></div>
                </div>

                {/* product section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="relative mb-1">
                      Product Name{" "}
                      <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                        *
                      </span>
                    </label>
                    <SelectBox
                      dataSource={[
                        ...(products?.data || []),
                        { name: "Other", productID: "Other" },
                      ]}
                      displayExpr="name"
                      valueExpr="productID"
                      value={values.productID}
                      searchEnabled={true} // Enable search
                      showClearButton={true} // Allow deselection
                      onValueChanged={(e) => {
                        setFieldValue("productID", e.value);

                        // Check if "Other" is selected
                        const isOther =
                          e.component.option("selectedItem")?.name === "Other";
                        setIsOtherProduct(isOther);
                        setFieldValue("productSizeID", null); // Reset size when product changes
                        setFieldValue("productQualityID", null); //
                        // Show or hide the "Other" field for Product Name

                        if (!isOther) {
                          setFieldValue("otherProductName", ""); // Clear the "Other" field
                          setFieldValue("otherProductSize", ""); // Clear the "Other" field
                          setFieldValue("otherProductQuality", ""); // Clear the "Other" field
                        }

                        setProductID(isOther ? null : e.value); // Avoid fetching sizes and qualities if "Other" is selected
                      }}
                      isValid={!(errors.productID && touched.productID)}
                      validationError={
                        errors.productID ? { message: errors.productID } : null
                      }
                    />
                  </div>

                  {/* Conditionally render "Other Product Name" field */}
                  {isOtherProduct ? (
                    <>
                      <div>
                        <label className="mb-1">Product Name</label>
                        <TextBox
                          value={values.otherProductName}
                          onValueChanged={(e) =>
                            setFieldValue("otherProductName", e.value)
                          }
                          isValid={
                            !(
                              errors.otherProductName &&
                              touched.otherProductName
                            )
                          }
                          placeholder="Enter Product Name..."
                          validationError={
                            errors.otherProductName
                              ? { message: "Enter Product Name" }
                              : null
                          }
                        />
                      </div>
                    </>
                  ) : null}

                  {/* Product Size */}
                  {isOtherProduct ? (
                    <div>
                      <label className="relative mb-1">
                        Product Size (kg){" "}
                        <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                          *
                        </span>
                      </label>
                      <TextBox
                        value={values.productSizeID}
                        onValueChanged={(e) =>
                          setFieldValue("productSizeID", e.value)
                        }
                        isValid={
                          !(errors.productSizeID && touched.productSizeID)
                        }
                        validationError={
                          errors.productSizeID
                            ? { message: errors.productSizeID }
                            : null
                        }
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="relative mb-1">
                        Product Size (kg){" "}
                        <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                          *
                        </span>
                      </label>
                      <SelectBox
                        dataSource={sizes?.data || []}
                        displayExpr="description"
                        valueExpr="productSizeID"
                        value={values.productSizeID}
                        onValueChanged={(e) =>
                          setFieldValue("productSizeID", e.value)
                        }
                        isValid={
                          !(errors.productSizeID && touched.productSizeID)
                        }
                        validationError={
                          errors.productSizeID
                            ? { message: errors.productSizeID }
                            : null
                        }
                      />
                    </div>
                  )}

                  {/* Product Quality */}
                  {/* {isOtherProduct || isOtherSupplier ? (
                                        <div>
                                            <label className='relative mb-1'>Product Quality <span className='text-red-500 text-lg absolute top-[-4px] right-[-10px]'>*</span></label>
                                            <TextBox
                                                value={values.productQualityID}
                                                onValueChanged={(e) => setFieldValue("productQualityID", e.value)}
                                                isValid={!(errors.productQualityID && touched.productQualityID)}
                                                validationError={errors.productQualityID ? { message: errors.productQualityID } : null}
                                            />
                                        </div>
                                    ) : ( */}

                  {isOtherProduct ? (
                    <div>
                      <label className="relative mb-1">
                        Quality{" "}
                        <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                          *
                        </span>
                      </label>
                      <TextBox
                        value={values.productQualityID}
                        onValueChanged={(e) =>
                          setFieldValue("productQualityID", e.value)
                        }
                        isValid={
                          !(errors.productQualityID && touched.productQualityID)
                        }
                        validationError={
                          errors.productQualityID
                            ? { message: errors.productQualityID }
                            : null
                        }
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="relative mb-1">
                        Quality{" "}
                        <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                          *
                        </span>
                      </label>
                      <SelectBox
                        dataSource={qualities?.data || []}
                        displayExpr="description"
                        valueExpr="productQualityID"
                        value={values.productQualityID}
                        onValueChanged={(e) =>
                          setFieldValue("productQualityID", e.value)
                        }
                        isValid={
                          !(errors.productQualityID && touched.productQualityID)
                        }
                        validationError={
                          errors.productQualityID
                            ? { message: errors.productQualityID }
                            : null
                        }
                      />
                    </div>
                  )}

                  <div>
                    <label className="mb-1">Sales Price / kg (€)</label>
                    <TextBox
                      value={values.salePricePerKG}
                      inputAttr={{ type: "number", step: "any", min: "1" }} // Allow decimal values
                      // Restricts to numbers only

                      onValueChanged={(e) =>
                        setFieldValue("salePricePerKG", e.value)
                      }
                      // Show validation messages as tooltips
                      isValid={
                        !(errors.salePricePerKG && touched.salePricePerKG)
                      } // Controls border color
                      validationError={
                        errors.salePricePerKG
                          ? { message: errors.salePricePerKG }
                          : null
                      }
                    />
                  </div>

                  {/* Conditionally render location as TextBox or SelectBox */}
                  {isOtherSupplier ? (
                    <div>
                      <label className="relative mb-1">
                        Location{" "}
                        <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                          *
                        </span>
                      </label>
                      <TextBox
                        value={values.locationID}
                        onValueChanged={(e) =>
                          setFieldValue("locationID", e.value)
                        }
                        placeholder="Enter Location..."
                        isValid={!(errors.locationID && touched.locationID)}
                        validationError={
                          errors.locationID
                            ? { message: errors.locationID }
                            : null
                        }
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="relative mb-1">
                        Location{" "}
                        <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                          *
                        </span>
                      </label>
                      <SelectBox
                        dataSource={values.availableLocations || []} // Use the dynamically set locations
                        displayExpr="addressLine" // Display the address line
                        valueExpr="supplierAddressID" // Use supplierAddressID as the value
                        value={values.locationID}
                        onValueChanged={(e) =>
                          setFieldValue("locationID", e.value)
                        }
                        isValid={!(errors.locationID && touched.locationID)}
                        validationError={
                          errors.locationID
                            ? { message: errors.locationID }
                            : null
                        }
                      />
                    </div>
                  )}

                  <div>
                    <label className="mb-1">Purchase Ref.</label>
                    <TextBox
                      value={values.purchaseRef}
                      onValueChanged={(e) =>
                        setFieldValue("purchaseRef", e.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="relative mb-1">
                      <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                        *
                      </span>
                      Batch No.
                    </label>
                    <TextBox
                      value={values.batchNumber}
                      onValueChanged={(e) =>
                        setFieldValue("batchNumber", e.value)
                      }
                      isValid={!(errors.batchNumber && touched.batchNumber)} // Controls border color
                      validationError={
                        errors.batchNumber
                          ? { message: errors.batchNumber }
                          : null
                      } // Tooltip error message
                    />
                  </div>
                </div>
                {/* product section */}
              </div>

              {/* <div className='dx-card responsive-paddings custom-base-accent'>
                                <div className='w-full flex items-center mb-2'>
                                    <p className="whitespace-nowrap text-xl font-medium">Market Place:</p>
                                    <div className="flex-grow h-[0.5px] bg-gray-300 ml-2 light-bg"></div>
                                </div>
                                <div className='dx-card responsive-paddings custom-base-accent'>
                                    <p className="whitespace-nowrap text-lg">Purchase</p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        {["Sup", "Ord", "Prod A", "Prod B", "Prod C", "Prod D", "Prod E"].map((field) => (
                                            <div key={field}>
                                                <label className='mb-1'>{field}</label>
                                                <TextBox
                                                    
                                                    onValueChanged={() => {  }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className='dx-card responsive-paddings mt-3 custom-base-accent'>
                                    <p className="whitespace-nowrap text-lg">Sales</p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        {["Trim A", "Trim B", "Trim C", "Trim D", "Trim E"].map((field) => (
                                            <div key={field}>
                                                <label className='mb-1'>{field}</label>
                                                <TextBox
                                                    
                                                    onValueChanged={() => { }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div> */}

              <>
                {/* Sentinel used to detect scroll position */}
                <div ref={sentinelRef} className="h-1" />

                {/* Spacer to prevent overlap */}
                {isSticky && <div className="h-16" />}

                {/* Sticky form footer */}
                <div
                  className={`flex justify-end p-3 form-footer-bg border-t border-gray-200 ${
                    isSticky ? "fixed bottom-0 left-0 right-0 z-10" : ""
                  }`}
                >
                  <Button
                    className="me-3"
                    width={100}
                    text="Cancel"
                    type="normal"
                    stylingMode="outlined"
                    onClick={() => navigate("/quality/fillet/overview")}
                  />
                  <Button
                    width={100}
                    text={loading ? "Saving..." : "Save"}
                    stylingMode="contained"
                    type="default"
                    useSubmitBehavior
                    onClick={() => console.log(`values`, errors)}
                    disabled={loading}
                  />
                </div>
              </>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default IntakeForm;
